import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useCompany';
import { Link } from 'react-router-dom';
import { User, Building2, Shield, Bell, Moon, Sun, Users, ChevronRight } from 'lucide-react';
import { useState } from 'react';

export default function Settings() {
  const { user } = useAuth();
  const { selectedCompany } = useCompany();
  const [darkMode, setDarkMode] = useState(false);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const canManageUsers = user?.role === 'ceo' || user?.role === 'admin';

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account and application preferences</p>
      </div>

      {/* User Management Link (CEO/Admin only) */}
      {canManageUsers && (
        <Link
          to="/settings/users"
          className="flex items-center justify-between p-6 card-surface hover:bg-muted/50 transition-colors"
          data-testid="user-management-link"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">User Management</h2>
              <p className="text-sm text-muted-foreground">Add, edit, and manage user access and roles</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </Link>
      )}

      {/* Profile Section */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-3 mb-6">
          <User className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Profile</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">First Name</label>
            <input
              type="text"
              value={user?.first_name || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Last Name</label>
            <input
              type="text"
              value={user?.last_name || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Role</label>
            <input
              type="text"
              value={user?.role?.toUpperCase() || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground capitalize"
            />
          </div>
        </div>
      </div>

      {/* Company Section */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Current Company</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Company Name</label>
            <input
              type="text"
              value={selectedCompany?.name || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Company Code</label>
            <input
              type="text"
              value={selectedCompany?.code || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Type</label>
            <input
              type="text"
              value={selectedCompany?.type === 'parent' ? 'Parent Company' : 'Subsidiary'}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground capitalize"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Industry</label>
            <input
              type="text"
              value={selectedCompany?.industry || ''}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-input bg-muted/50 text-foreground"
            />
          </div>
        </div>
      </div>

      {/* Preferences Section */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Preferences</h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div className="flex items-center gap-3">
              {darkMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">Toggle dark/light theme</p>
              </div>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                darkMode ? 'bg-primary' : 'bg-muted'
              }`}
              data-testid="dark-mode-toggle"
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  darkMode ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="card-surface p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Security</h2>
        </div>
        <div className="space-y-4">
          <button className="w-full text-left px-4 py-3 rounded-lg border border-input hover:bg-muted transition-colors">
            <p className="font-medium">Change Password</p>
            <p className="text-sm text-muted-foreground">Update your account password</p>
          </button>
          <button className="w-full text-left px-4 py-3 rounded-lg border border-input hover:bg-muted transition-colors">
            <p className="font-medium">Two-Factor Authentication</p>
            <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
          </button>
        </div>
      </div>
    </div>
  );
}
